import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { RankmiTable } from '@rankmi/rankmi-table'
import { faBook } from '@fortawesome/pro-solid-svg-icons/faBook'
import { useTranslation } from 'react-i18next'
import EmptyState from 'components/reusables/emptyState'
import { ModalButtons } from 'components/reusables/modalButtons'
import { Modal } from 'components/reusables/modal'
import Profiles from 'components/reusables/profiles'
import { dc3AttestationsService } from 'api/administration/dc3Attestations'
import { IDc3AssociatedCourse } from 'interfaces/dc3Attestation'
import { IProfile } from 'interfaces/profiles'
import { RankmiTableWrapper, Wrapper } from './styled'

interface IProps {
  excludedCourseIds: number[]
  onClose: () => void
  onAssociate: (courses: IDc3AssociatedCourse[]) => void
}

const PER_PAGE = 10

const AssociateNormativeCoursesModal = ({
  excludedCourseIds,
  onClose,
  onAssociate,
}: IProps) => {
  const { t } = useTranslation()
  const history = useHistory()
  const prefix =
    'administration.certificate.legalAttestationAssociateCourses.modal'
  const [courses, setCourses] = useState<IDc3AssociatedCourse[]>([])
  const [selectedCourses, setSelectedCourses] = useState<
    IDc3AssociatedCourse[]
  >([])
  const [loading, setLoading] = useState(false)
  const [searchCriteria, setSearchCriteria] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)

  const excludedCourseIdSet = useMemo(
    () => new Set(excludedCourseIds),
    [excludedCourseIds]
  )

  const removeExcludedCourses = useCallback(
    (availableCourses: IDc3AssociatedCourse[]) =>
      availableCourses
        .filter((course) => !excludedCourseIdSet.has(course.id))
        .map((course) => ({
          ...course,
          key: course.id,
        })),
    [excludedCourseIdSet]
  )

  const fetchCourses = useCallback(
    async (page: number, criteria: string) => {
      setLoading(true)
      try {
        const response =
          await dc3AttestationsService.getAvailableNormativeCourses({
            page,
            perPage: PER_PAGE,
            search: criteria || undefined,
            excludeCourseIds: excludedCourseIds,
          })

        const filteredCourses = removeExcludedCourses(response.data)

        setCourses(filteredCourses)
        setTotalRecords(response.total)
      } finally {
        setLoading(false)
      }
    },
    [excludedCourseIds, removeExcludedCourses]
  )

  useEffect(() => {
    fetchCourses(1, '')
  }, [fetchCourses])

  const onAdd = () => {
    onAssociate(selectedCourses)
    onClose()
  }

  const onSearch = (value: string) => {
    setSearchCriteria(value)
    setCurrentPage(1)
    fetchCourses(1, value)
  }

  const onPaginationChange = (value: number) => {
    setCurrentPage(value)
    fetchCourses(value, searchCriteria)
  }

  const onRowSelected = (value: {
    dispatcher?: string
    selectedRows: IDc3AssociatedCourse[]
    selectedRowKeys: number[]
  }) => {
    if (value.dispatcher === 'handleUnselectAllRowsOnFilter') return

    const courseList = [...selectedCourses, ...value.selectedRows]
    const addedCourses: number[] = []

    setSelectedCourses(
      courseList.filter((course) => {
        if (
          value.selectedRowKeys.includes(course.id) &&
          !addedCourses.includes(course.id)
        ) {
          addedCourses.push(course.id)
          return true
        }
        return false
      })
    )
  }

  const locale = useMemo(
    () => ({
      header: {
        rowsCount: t(`${prefix}.rowsCount`),
        rowsCount_plural: t(`${prefix}.rowsCount_plural`),
        search: t('administration.courses.list.search'),
      },
    }),
    [t, prefix]
  )

  const handleCreateNormativeCourse = useCallback(() => {
    onClose()
    history.push('/administration/courses/new?normative=1')
  }, [history, onClose])

  const catalogEmpty = useMemo(
    () => (
      <EmptyState
        title={t(`${prefix}.emptyState.title`)}
        description={t(`${prefix}.emptyState.description`)}
        icon={faBook}
        action={handleCreateNormativeCourse}
        actionTitle={t(`${prefix}.emptyState.action`)}
        actionBackground={false}
      />
    ),
    [t, prefix, handleCreateNormativeCourse]
  )

  const tableEmpty = useMemo(() => {
    if (!searchCriteria.trim() && totalRecords === 0) return catalogEmpty

    return <EmptyState title={t(`${prefix}.searchEmpty`)} icon={faBook} />
  }, [searchCriteria, totalRecords, catalogEmpty, t, prefix])

  const columns = useMemo(
    () => [
      {
        title: t(`${prefix}.columns.course`),
        render: (course: IDc3AssociatedCourse) => course.name,
      },
      {
        title: t(`${prefix}.columns.profiles`),
        dataIndex: 'lmsProfiles',
        key: 'lmsProfiles',
        render: (lmsProfiles: IProfile[]) => <Profiles items={lmsProfiles} />,
      },
    ],
    [t, prefix]
  )

  return (
    <Modal
      title={t(`${prefix}.title`)}
      actions={
        <ModalButtons
          primaryText={t(`${prefix}.associate`)}
          primaryAction={onAdd}
          disablePrimaryButton={selectedCourses.length === 0}
          secondaryText={t(`${prefix}.cancel`)}
          secondaryAction={onClose}
        />
      }
      maxheight={500}
      maxwidth={800}
      close={onClose}
    >
      <Wrapper>
        <RankmiTableWrapper>
          <RankmiTable
            locale={locale}
            columnHeaders={columns}
            data={courses}
            total={totalRecords}
            search={searchCriteria}
            onSearch={onSearch}
            onRowSelected={onRowSelected}
            onPaginationChange={onPaginationChange}
            selectedRows={selectedCourses.map((row) => row.id)}
            currentPage={currentPage}
            countIcon={faBook}
            loading={loading}
            delaySearch
            customEmpty={tableEmpty}
            showFooter={totalRecords > 0}
          />
        </RankmiTableWrapper>
      </Wrapper>
    </Modal>
  )
}

export default AssociateNormativeCoursesModal
